import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { ApolloProvider, gql, useMutation } from '@apollo/client';
import apolloClient from 'common/ApolloClient';
import clsx from 'clsx';
import I18n from 'i18n-js';
import styles from './LanguageToggle.module.scss';

const getLanguage = (locale, collapsed, abbreviate = false, menuValue = '') => {
  if (collapsed || abbreviate) return locale === 'en' ? 'EN' : 'ES';

  if (menuValue === '') return locale === 'en' ? 'English' : 'Español';

  return menuValue === 'en' ? I18n.t('english') : I18n.t('spanish');
};

const LanguageToggleWrapper = ({ locale, copyright, colorMode, collapsed, isAdminEnvironment }) => (
  <ApolloProvider client={apolloClient}>
    <LanguageToggle
      collapsed={collapsed}
      colorMode={colorMode}
      copyright={copyright}
      isAdminEnvironment={isAdminEnvironment}
      locale={locale}
    />
  </ApolloProvider>
);

LanguageToggleWrapper.propTypes = {
  collapsed: PropTypes.bool.isRequired,
  colorMode: PropTypes.string.isRequired,
  copyright: PropTypes.string.isRequired,
  isAdminEnvironment: PropTypes.bool,
  locale: PropTypes.string.isRequired,
};

LanguageToggleWrapper.defaultProps = {
  isAdminEnvironment: false,
};

const SAVE_LOCALE = gql`
mutation SaveLocale($locale: String!) {
  updateLocale(locale: $locale) {
    success
  }
}
`;

const reload = () => window.location.reload();

const Spinner = () => <i aria-hidden className="fa fa-spinner fa-spin mr5" />;

const LanguageToggle = ({ locale: initialLocale, copyright, colorMode, collapsed, isAdminEnvironment }) => {
  const [locale, setLocale] = useState(initialLocale);
  const [saveLocale] = useMutation(SAVE_LOCALE);
  const [loading, setLoading] = useState(false);

  const updateLocale = (newLocale) => {
    if (newLocale === locale) return;

    setLocale(newLocale);
    setLoading(true);
    saveLocale({ variables: { locale: newLocale } }).then(reload);
  };

  return (
    <div className={clsx('dsp-flex-center', {
      [`${styles.bottomContainer} tw-fixed tw-bottom-0`]: !isAdminEnvironment,
      'tw-bg-inherit tw-border-t-0 tw-pt-2': isAdminEnvironment })}
    >
      <div className={clsx('dropdown', {
        'tw-left-2.5': isAdminEnvironment,
        'tw-left-3': !isAdminEnvironment })}
      >
        <button
          aria-expanded="false"
          aria-label={`${I18n.t('language_selection')} - ${getLanguage(locale, false, isAdminEnvironment, 'en')}`}
          className={clsx([`${styles.button} dropdown-toggle`], {
            'tw-p-0': isAdminEnvironment,
          })}
          data-toggle="dropdown"
          disabled={loading}
          type="button"
          onClick={e => e.stopPropagation()}
        >
          {loading && <Spinner />}
          {getLanguage(locale, collapsed, isAdminEnvironment)}
        </button>
        <ul className={clsx('dropdown-menu', {
          [styles.dropdownMenu]: !isAdminEnvironment })}
        >
          <li><LanguageOption collapsed={collapsed} locale={locale} setLocale={updateLocale} value="en" /></li>
          <li><LanguageOption collapsed={collapsed} locale={locale} setLocale={updateLocale} value="es" /></li>
        </ul>
      </div>
      {!isAdminEnvironment && (
        <div
          className={clsx('tw-font-tci tw-left-12 tw-relative', {
            hidden: collapsed,
            [styles.copyrightLight]: colorMode === 'light',
            [styles.copyrightDark]: colorMode !== 'light',
          })}
        >
          &copy;
          &nbsp;
          {copyright}
        </div>
      )}
    </div>
  );
};

LanguageToggle.propTypes = {
  collapsed: PropTypes.bool.isRequired,
  colorMode: PropTypes.string.isRequired,
  copyright: PropTypes.string.isRequired,
  isAdminEnvironment: PropTypes.bool,
  locale: PropTypes.string.isRequired,
};

LanguageToggle.defaultProps = {
  isAdminEnvironment: false,
};

const LanguageOption = ({ locale, setLocale, value, collapsed }) => {
  const selected = value === locale;

  return (
    <button
      aria-current={locale === value ? 'true' : undefined}
      aria-label={I18n.t('change_site_language', { language: getLanguage(locale, false, false, value) })}
      className={`${styles.option} ${selected ? '' : styles.notSelected} dropdown-item full-size`}
      type="button"
      onClick={(e) => {
        setLocale(value);
        e.stopPropagation();
      }}
    >
      {selected && <i className={`${styles.check} fa fa-check`} />}
      {getLanguage(value, collapsed)}
    </button>
  );
};

LanguageOption.propTypes = {
  collapsed: PropTypes.bool.isRequired,
  locale: PropTypes.string.isRequired,
  setLocale: PropTypes.func.isRequired,
  value: PropTypes.string.isRequired,
};

export default LanguageToggleWrapper;
